import React from "react";
import { Row, Col } from "react-bootstrap";
import { Icon } from "@iconify/react";
import styles from './AccessosWiki.module.scss';

/**
 * 📦 Wiki - API de Produtos
 * 
 * Documentação sobre como usar a API para gerenciar produtos.
 * Explica de forma simples como criar, editar e consultar produtos via API.
 * Voltada para produtores leigos (não técnica).
 */
const ApiProdutosWiki = () => {
  return (
    <div className={styles.wikiAccessos}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBadge}>
          <Icon icon="mdi:package-variant" />
          <span>API de Produtos</span>
        </div>
        <p className={styles.heroText}>
          Gerencie seus produtos automaticamente via API. Crie produtos, atualize informações, 
          gerencie planos e muito mais, tudo sem precisar acessar o painel manualmente.
        </p>
      </section>

      {/* O que é a API de Produtos */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>O que é a API de Produtos?</span>
          <p className={styles.sectionSubtitle}>
            Entenda como funciona
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:information" className={styles.infoIcon} />
          <div>
            <p>
              A <strong>API de Produtos</strong> permite que você <strong>gerencie produtos automaticamente</strong> 
              através de sistemas externos. Em vez de acessar o painel manualmente para criar ou editar um produto, 
              seu sistema pode fazer isso automaticamente via API.
            </p>
            <p style={{ marginTop: '12px', marginBottom: 0 }}>
              Por exemplo: quando você adiciona um novo produto no seu sistema de gestão, ele pode 
              <strong>criar o produto automaticamente</strong> na Pagmus, atualizar preços quando houver mudanças, 
              e muito mais. Tudo <strong>sem intervenção manual</strong>!
            </p>
          </div>
        </div>
      </section>

      {/* O que você pode fazer */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>O que você pode fazer?</span>
          <p className={styles.sectionSubtitle}>
            Funcionalidades disponíveis
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconSuccess}`}>
                <Icon icon="mdi:plus-circle" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeSuccess}`}>Criar</div>
                <span className={styles.cardTitle}>Criar Produtos</span>
                <p>
                  <strong>Crie produtos automaticamente</strong> com todas as informações: nome, descrição, 
                  preço, categoria, imagem e muito mais. O produto inicia como "pendente de aprovação".
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconPlatform}`}>
                <Icon icon="mdi:pencil" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgePlatform}`}>Editar</div>
                <span className={styles.cardTitle}>Atualizar Produtos</span>
                <p>
                  <strong>Atualize informações</strong> de produtos existentes. Produtos aprovados têm 
                  limitações para proteger a integridade dos dados.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconWarning}`}>
                <Icon icon="mdi:toggle-switch" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeWarning}`}>Status</div>
                <span className={styles.cardTitle}>Atualizar Status</span>
                <p>
                  <strong>Ative ou desative</strong> produtos automaticamente. Mude o status entre 
                  "ativo", "inativo" ou "rascunho" conforme necessário.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconAffiliate}`}>
                <Icon icon="mdi:format-list-bulleted" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeAffiliate}`}>Planos</div>
                <span className={styles.cardTitle}>Gerenciar Planos</span>
                <p>
                  <strong>Crie, edite e delete</strong> planos de produtos automaticamente. Gerencie 
                  preços, quantidades e recorrências via API.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconSuccess}`}>
                <Icon icon="mdi:eye" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeSuccess}`}>Consultar</div>
                <span className={styles.cardTitle}>Listar e Buscar</span>
                <p>
                  <strong>Busque e liste</strong> todos os seus produtos. Use filtros para encontrar 
                  produtos específicos por categoria, status, aprovação, etc.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Como funciona */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Funciona?</span>
          <p className={styles.sectionSubtitle}>
            Fluxo simplificado de criação de produto via API
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>1</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:package-variant-closed-plus" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Sistema Prepara Dados
                </span>
                <p>
                  Seu sistema prepara todas as informações do produto: nome, descrição, preço, categoria, 
                  imagem, e outras configurações.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>2</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:send" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Sistema Envia para API
                </span>
                <p>
                  Seu sistema <strong>envia uma requisição</strong> para a API da Pagmus criando o produto. 
                  A API valida os dados e processa a criação.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>3</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:check-circle" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  API Cria Produto
                </span>
                <p>
                  A API <strong>cria o produto</strong> na Pagmus com status "pendente de aprovação". 
                  Retorna um ID único do produto criado.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>4</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:account-check" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Aguarda Aprovação
                </span>
                <p>
                  O produto fica <strong>aguardando aprovação</strong> do administrador. Após aprovado, 
                  ele fica disponível para vendas.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Informações necessárias */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Quais Informações são Necessárias?</span>
          <p className={styles.sectionSubtitle}>
            Dados obrigatórios e opcionais para criar um produto
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:file-document-edit" className={styles.infoIcon} />
          <div>
            <p>
              Para criar um produto via API, você precisa informar:
            </p>
            <ul style={{ marginTop: '12px', marginBottom: '12px', paddingLeft: '20px' }}>
              <li><strong>Nome:</strong> Nome do produto (obrigatório)</li>
              <li><strong>Preço:</strong> Valor do produto em reais (obrigatório)</li>
              <li><strong>Categoria:</strong> Categoria do produto (opcional, padrão: "Geral")</li>
            </ul>
            <p style={{ marginTop: '12px', marginBottom: 0 }}>
              <strong>Opcionalmente</strong>, você pode informar: descrição, imagem, valor de comissão, 
              tipo de comissão, URLs (página de vendas, obrigado, Reclame Aqui), email e telefone de 
              suporte, e dias de garantia.
            </p>
          </div>
        </div>
      </section>

      {/* Regras importantes */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Regras Importantes</span>
          <p className={styles.sectionSubtitle}>
            O que você precisa saber antes de usar
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:clock-outline" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Produto Inicia Pendente
                </span>
                <p>
                  Produtos criados via API iniciam com status <strong>"pendente de aprovação"</strong>. 
                  Eles precisam ser aprovados pelo administrador antes de ficarem disponíveis para vendas.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:lock" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Produtos Aprovados são Protegidos
                </span>
                <p>
                  Produtos já aprovados têm <strong>proteções especiais</strong>. Você só pode editar 
                  campos não críticos (descrição, imagem, URLs, suporte). Nome, preço e categoria não 
                  podem ser alterados.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:account-check" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Apenas Seus Produtos
                </span>
                <p>
                  Você só pode criar e editar <strong>produtos que você mesmo criou</strong>. Não é 
                  possível modificar produtos de outros produtores.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:delete-alert" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Planos com Vendas Não Podem Ser Deletados
                </span>
                <p>
                  Se um plano tiver <strong>vendas associadas</strong>, ele não pode ser deletado via API. 
                  Isso protege a integridade dos dados históricos.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Gerenciamento de Planos */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Gerenciamento de Planos</span>
          <p className={styles.sectionSubtitle}>
            Como criar, editar e deletar planos via API
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconSuccess}`}>
                <Icon icon="mdi:plus-circle" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeSuccess}`}>Criar</div>
                <span className={styles.cardTitle}>Criar Planos</span>
                <p>
                  Crie planos para um produto informando: nome, preço, quantidade, parcelamento e 
                  tipo de recorrência. Cada produto pode ter múltiplos planos.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconPlatform}`}>
                <Icon icon="mdi:pencil" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgePlatform}`}>Editar</div>
                <span className={styles.cardTitle}>Atualizar Planos</span>
                <p>
                  Atualize informações de planos existentes: preço, quantidade, parcelamento, 
                  recorrência e status. Mantenha seus planos sempre atualizados!
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconWarning}`}>
                <Icon icon="mdi:delete" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeWarning}`}>Deletar</div>
                <span className={styles.cardTitle}>Remover Planos</span>
                <p>
                  Delete planos que não são mais necessários. <strong>Atenção:</strong> Planos com 
                  vendas ativas não podem ser deletados para proteger a integridade dos dados.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Como acessar a documentação */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Acessar a Documentação Técnica?</span>
          <p className={styles.sectionSubtitle}>
            Onde encontrar exemplos e detalhes técnicos
          </p>
        </div>

        <Row className="g-4">
          <Col lg={12}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>1</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:menu" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Menu Lateral
                </span>
                <p>
                  No <strong>menu lateral</strong>, acesse <strong>Opções → API → Produtos</strong>. 
                  Lá você encontrará a documentação técnica completa com exemplos de código, 
                  endpoints disponíveis e como usar cada funcionalidade.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Dicas */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Dicas e Boas Práticas</span>
          <p className={styles.sectionSubtitle}>
            Recomendações para usar a API de Produtos
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:test-tube" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Teste Primeiro
                </span>
                <p>
                  Sempre <strong>teste a integração</strong> em ambiente de desenvolvimento antes de 
                  usar em produção. Crie produtos de teste primeiro!
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:sync" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Mantenha Sincronizado
                </span>
                <p>
                  Quando houver mudanças de preço ou informações no seu sistema, <strong>atualize 
                  imediatamente</strong> via API para manter tudo sincronizado.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:file-document-check" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Valide Dados
                </span>
                <p>
                  <strong>Valide os dados</strong> no seu sistema antes de enviar para a API. 
                  Verifique se preços são válidos e se campos obrigatórios estão preenchidos.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:account-clock" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Aguarde Aprovação
                </span>
                <p>
                  Lembre-se que produtos criados via API ficam <strong>pendentes de aprovação</strong>. 
                  Acompanhe o status e aguarde a aprovação antes de começar a vender.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* FAQ */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Perguntas Frequentes</span>
          <p className={styles.sectionSubtitle}>
            Tire suas dúvidas sobre a API de Produtos
          </p>
        </div>

        <Row className="g-4">
          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Produtos criados via API precisam ser aprovados?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Sim. Produtos criados via API iniciam com status <strong>"pendente de aprovação"</strong>. 
                  Eles precisam ser aprovados pelo administrador antes de ficarem disponíveis para vendas. 
                  Isso garante a qualidade dos produtos na plataforma.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Posso editar nome e preço de um produto já aprovado?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Não. Produtos aprovados têm <strong>proteções especiais</strong>. Você só pode editar 
                  campos não críticos como: descrição, imagem, URLs, email e telefone de suporte, e dias 
                  de garantia. Nome, preço e categoria não podem ser alterados para proteger a integridade.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Posso deletar um plano que tem vendas?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Não. Planos com <strong>vendas associadas</strong> não podem ser deletados via API. 
                  Isso protege a integridade dos dados históricos. Se precisar desativar um plano, 
                  você pode atualizar seu status para "inativo" ao invés de deletá-lo.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Preciso de permissão especial para usar a API de Produtos?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Sim. Ao criar um token de API, você precisa configurar a permissão 
                  <strong>"products:write"</strong> para criar e editar produtos. Sem essa permissão, 
                  você só poderá consultar (ler) produtos, mas não criar ou editar.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>
    </div>
  );
};

export default ApiProdutosWiki;

